/**
 * Client HTTP de l'API analytics.
 *
 * `fetch` nu, sans axios : trois routes, aucun intercepteur à installer.
 *
 * L'URL de base vient de `VITE_API_URL`, figée au build (cf. `.env.example`).
 */

import type {
  AnalysisEnvelope,
  CatalogStatsResponse,
  EngagementStatsResponse,
  HealthResponse,
  LearningStatsResponse,
  MarkerInput,
  MarkerListResponse,
  MarkerResponse,
  PeriodQuery,
  PeriodStatsResponse,
  PersonalizationStatsResponse,
  SessionStatsResponse,
  SubscriptionStatsResponse,
} from '@shared/types/stats';
import type { AccountResponse, LoginStep } from '@shared/types/auth';
import type { ChatRequest, ChatStreamEvent } from '@shared/types/chat';
import type { BtobSaleInput, BtobSaleResponse, BtobStatsResponse } from '@shared/types/btob';
import type { SavMessagesResponse, SavStatsResponse } from '@shared/types/sav';
import type { Ga4OverviewResponse } from '@shared/types/ga4';
import type { TrafficStatsResponse } from '@shared/types/traffic';
import type { BrandingExpenseInput, BrandingExpenseResponse, BrandingStatsResponse } from '@shared/types/branding';
import type { ChargesStatsResponse, FixedChargeInput, FixedChargeResponse } from '@shared/types/charges';
import type {
  ArticleCostInput,
  ArticleCostsResponse,
  BusinessSettingsInput,
  BusinessSettingsResponse,
  KitCostPeriodInput,
  KitCostPeriodsResponse,
  SubscriptionDeductionInput,
  SubscriptionDeductionsResponse,
} from '@shared/types/settings';
import type { AdsDailyKpisResponse, AdsStatsResponse, AmazonAdsStatsResponse, AmazonStatsResponse, SalesQuery, SalesStatsResponse, SubscriptionSalesStatsResponse } from '@shared/types/sales';

// Le slash final est retire une fois pour toutes : sinon chaque appel doit s'en
// mefier, et `//api/stats` passe chez certains proxys mais pas chez d'autres.
const BASE_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

if (!BASE_URL) {
  // Pas un throw : l'application doit s'afficher et dire ce qui manque, plutot
  // que de rendre une page blanche sur une erreur de module.
  console.error(
    'VITE_API_URL est vide — copier .env.example en .env et relancer le build.',
  );
}

/**
 * Erreur d'API.
 *
 * `status` est exposé pour que la couche au-dessus distingue un 401 (jeton
 * expiré → déconnexion) d'un 400 (période invalide → message à corriger).
 */
export class ApiRequestError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  token?: string | undefined;
  signal?: AbortSignal | undefined;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, token, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    // fetch ne rejette que sur un echec reseau : API arretee, DNS, CORS.
    // Le distinguer d'une erreur HTTP evite le message « Erreur 0 » classique.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiRequestError(0, "API injoignable. Vérifier qu'elle tourne et que VITE_API_URL est correct.");
  }

  if (!response.ok) {
    // Toutes les erreurs de l'API ont la forme { error: string }. Un proxy mal
    // configure peut renvoyer du HTML : on retombe alors sur le statut.
    let message = `Erreur ${response.status}`;
    try {
      const payload = (await response.json()) as { error?: unknown };
      if (typeof payload.error === 'string' && payload.error) message = payload.error;
    } catch {
      // Corps illisible : on garde le message par defaut.
    }
    throw new ApiRequestError(response.status, message);
  }

  // 204 : DELETE /api/markers/:id ne renvoie pas de corps, et `response.json()`
  // leverait sur une reponse vide. L'appelant type alors T a `void`.
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}

// --- Connexion en plusieurs pas (cf. shared/types/auth.ts) -------------------

export function login(email: string, password: string, deviceToken?: string, signal?: AbortSignal): Promise<LoginStep> {
  return request<LoginStep>('/api/auth/login', {
    method: 'POST',
    body: { email, password, ...(deviceToken ? { deviceToken } : {}) },
    signal,
  });
}

/** Nouveau mot de passe imposé à la connexion : renvoie le pas suivant. */
export function changePasswordWithTicket(ticket: string, newPassword: string): Promise<LoginStep> {
  return request<LoginStep>('/api/auth/password', { method: 'POST', body: { ticket, newPassword } });
}

/** Changement volontaire (page Compte) : session + mot de passe actuel. */
export function changePassword(token: string, currentPassword: string, newPassword: string): Promise<void> {
  return request<void>('/api/auth/password', { method: 'POST', body: { currentPassword, newPassword }, token });
}

export function verifyMfa(ticket: string, code: string, rememberDevice: boolean): Promise<LoginStep> {
  return request<LoginStep>('/api/auth/mfa/verify', { method: 'POST', body: { ticket, code, rememberDevice } });
}

export function fetchAccount(token: string, signal?: AbortSignal): Promise<AccountResponse> {
  return request<AccountResponse>('/api/auth/me', { token, signal });
}

export function forgetTrustedDevices(token: string): Promise<void> {
  return request<void>('/api/auth/devices/forget', { method: 'POST', token });
}

export function revokeAllSessions(token: string): Promise<void> {
  return request<void>('/api/auth/sessions/revoke', { method: 'POST', token });
}

export function fetchPeriodStats(
  query: PeriodQuery,
  token: string,
  signal?: AbortSignal,
): Promise<PeriodStatsResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  if (query.topTracksLimit !== undefined) {
    params.set('topTracksLimit', String(query.topTracksLimit));
  }
  return request<PeriodStatsResponse>(`/api/stats/period?${params.toString()}`, {
    token,
    signal,
  });
}

/** Paramètres de période, communs aux trois routes de statistiques. */
function periodParams(query: PeriodQuery): string {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  if (query.topTracksLimit !== undefined) {
    params.set('topTracksLimit', String(query.topTracksLimit));
  }
  return params.toString();
}

export function fetchLearningStats(
  query: PeriodQuery,
  token: string,
  signal?: AbortSignal,
): Promise<LearningStatsResponse> {
  return request<LearningStatsResponse>(`/api/stats/learning?${periodParams(query)}`, { token, signal });
}

export function fetchPersonalizationStats(
  query: PeriodQuery,
  token: string,
  signal?: AbortSignal,
): Promise<PersonalizationStatsResponse> {
  return request<PersonalizationStatsResponse>(
    `/api/stats/personalization?${periodParams(query)}`,
    { token, signal },
  );
}

export function fetchSessionStats(
  query: PeriodQuery,
  token: string,
  signal?: AbortSignal,
): Promise<SessionStatsResponse> {
  return request<SessionStatsResponse>(`/api/stats/sessions?${periodParams(query)}`, { token, signal });
}

export function fetchCatalogStats(
  query: PeriodQuery,
  token: string,
  signal?: AbortSignal,
): Promise<CatalogStatsResponse> {
  return request<CatalogStatsResponse>(`/api/stats/catalog?${periodParams(query)}`, { token, signal });
}

export function fetchEngagementStats(
  query: PeriodQuery,
  token: string,
  signal?: AbortSignal,
): Promise<EngagementStatsResponse> {
  return request<EngagementStatsResponse>(`/api/stats/engagement?${periodParams(query)}`, { token, signal });
}

/**
 * Une analyse explicative, par son nom de route.
 *
 * Générique plutôt que cinq fonctions quasi identiques : seule l'URL change, et
 * le type de charge utile est porté par l'appelant.
 */
export function fetchAnalysis<T>(
  name: string,
  query: PeriodQuery,
  token: string,
  signal?: AbortSignal,
): Promise<{ stats: AnalysisEnvelope<T> }> {
  return request<{ stats: AnalysisEnvelope<T> }>(
    `/api/stats/analysis/${name}?${periodParams(query)}`,
    { token, signal },
  );
}

export function fetchSubscriptionStats(
  query: PeriodQuery,
  token: string,
  signal?: AbortSignal,
): Promise<SubscriptionStatsResponse> {
  return request<SubscriptionStatsResponse>(
    `/api/stats/subscription?${periodParams(query)}`,
    { token, signal },
  );
}

/**
 * Ventes Shopify. Le rattachement des remboursements n'est envoyé que s'il
 * diffère du défaut : l'URL reste courte dans les journaux du proxy.
 */
export function fetchSalesStats(
  query: SalesQuery,
  token: string,
  signal?: AbortSignal,
): Promise<SalesStatsResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  if (query.refundBasis && query.refundBasis !== 'refund_date') {
    params.set('refundBasis', query.refundBasis);
  }
  return request<SalesStatsResponse>(`/api/sales?${params.toString()}`, { token, signal });
}

/** Abonnements Stripe, côté ventes. Route à part : cf. le contrôleur. */
export function fetchSubscriptionSalesStats(
  query: PeriodQuery,
  token: string,
  signal?: AbortSignal,
): Promise<SubscriptionSalesStatsResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  return request<SubscriptionSalesStatsResponse>(`/api/sales/subscriptions?${params.toString()}`, { token, signal });
}

/** Publicité Meta, côté ventes. */
export function fetchAdsStats(query: PeriodQuery, token: string, signal?: AbortSignal): Promise<AdsStatsResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  return request<AdsStatsResponse>(`/api/sales/ads?${params.toString()}`, { token, signal });
}

/** Réglages métier (taux de marge…), saisis depuis le dashboard. */
export function fetchBusinessSettings(token: string, signal?: AbortSignal): Promise<BusinessSettingsResponse> {
  return request<BusinessSettingsResponse>('/api/settings', { token, signal });
}

export function saveBusinessSettings(input: BusinessSettingsInput, token: string): Promise<BusinessSettingsResponse> {
  return request<BusinessSettingsResponse>('/api/settings', { method: 'PUT', body: input, token });
}

/** Ads KPIs jour par jour, `days` jours se terminant à `to` (hier par défaut). */
export function fetchAdsDailyKpis(
  to: string | null,
  token: string,
  signal?: AbortSignal,
  days?: number,
): Promise<AdsDailyKpisResponse> {
  const params = new URLSearchParams();
  if (to) params.set('to', to);
  if (days !== undefined) params.set('days', String(days));
  const suffix = params.toString();
  return request<AdsDailyKpisResponse>(`/api/sales/ads/daily${suffix ? `?${suffix}` : ''}`, { token, signal });
}

/** Ventes Amazon (Selling Partner API). */
export function fetchAmazonStats(query: PeriodQuery, token: string, signal?: AbortSignal): Promise<AmazonStatsResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  return request<AmazonStatsResponse>(`/api/sales/amazon?${params.toString()}`, { token, signal });
}

/** Publicité Amazon (Ads API). */
export function fetchAmazonAdsStats(query: PeriodQuery, token: string, signal?: AbortSignal): Promise<AmazonAdsStatsResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  return request<AmazonAdsStatsResponse>(`/api/sales/amazon-ads?${params.toString()}`, { token, signal });
}

/** Google Ads : même réponse qu'Amazon Ads (`adProduct` = type de campagne). */
export function fetchGoogleAdsStats(query: PeriodQuery, token: string, signal?: AbortSignal): Promise<AmazonAdsStatsResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  return request<AmazonAdsStatsResponse>(`/api/sales/google-ads?${params.toString()}`, { token, signal });
}

// -----------------------------------------------------------------------------
// BtoB — ventes saisies à la main.
// -----------------------------------------------------------------------------

export function fetchBtobStats(query: PeriodQuery, token: string, signal?: AbortSignal): Promise<BtobStatsResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  return request<BtobStatsResponse>(`/api/btob/stats?${params.toString()}`, { token, signal });
}

export function createBtobSale(input: BtobSaleInput, token: string): Promise<BtobSaleResponse> {
  return request<BtobSaleResponse>('/api/btob/sales', { method: 'POST', body: input, token });
}

export function updateBtobSale(id: number, input: BtobSaleInput, token: string): Promise<BtobSaleResponse> {
  return request<BtobSaleResponse>(`/api/btob/sales/${id}`, { method: 'PUT', body: input, token });
}

export function deleteBtobSale(id: number, token: string): Promise<void> {
  return request<void>(`/api/btob/sales/${id}`, { method: 'DELETE', token });
}

export function fetchHealth(signal?: AbortSignal): Promise<HealthResponse> {
  return request<HealthResponse>('/health', { signal });
}

// -----------------------------------------------------------------------------
// Marqueurs — la seule ressource que le dashboard écrit.
// -----------------------------------------------------------------------------

/**
 * Tous les marqueurs, sans filtre de période.
 *
 * Il y en a quelques dizaines au plus : le front les charge une fois et les
 * découpe lui-même selon la période affichée. Une requête par période
 * multiplierait les entrées de cache pour économiser quelques centaines
 * d'octets.
 */
export function fetchMarkers(token: string, signal?: AbortSignal): Promise<MarkerListResponse> {
  return request<MarkerListResponse>('/api/markers', { token, signal });
}

export function createMarker(input: MarkerInput, token: string): Promise<MarkerResponse> {
  return request<MarkerResponse>('/api/markers', { method: 'POST', body: input, token });
}

/** PUT et non PATCH : le formulaire renvoie l'objet entier, l'API remplace tout. */
export function updateMarker(
  id: number,
  input: MarkerInput,
  token: string,
): Promise<MarkerResponse> {
  return request<MarkerResponse>(`/api/markers/${id}`, { method: 'PUT', body: input, token });
}

export function deleteMarker(id: number, token: string): Promise<void> {
  return request<void>(`/api/markers/${id}`, { method: 'DELETE', token });
}

/** Retenues sur le CA des abonnements (Sacem…). Chaque écriture renvoie la liste entière. */
export function fetchDeductions(token: string, signal?: AbortSignal): Promise<SubscriptionDeductionsResponse> {
  return request<SubscriptionDeductionsResponse>('/api/settings/deductions', { token, signal });
}

export function createDeduction(input: SubscriptionDeductionInput, token: string): Promise<SubscriptionDeductionsResponse> {
  return request<SubscriptionDeductionsResponse>('/api/settings/deductions', { method: 'POST', body: input, token });
}

export function updateDeduction(id: number, input: SubscriptionDeductionInput, token: string): Promise<SubscriptionDeductionsResponse> {
  return request<SubscriptionDeductionsResponse>(`/api/settings/deductions/${id}`, { method: 'PUT', body: input, token });
}

export function deleteDeduction(id: number, token: string): Promise<SubscriptionDeductionsResponse> {
  return request<SubscriptionDeductionsResponse>(`/api/settings/deductions/${id}`, { method: 'DELETE', token });
}

/** Coût de revient d'un kit, par période. Chaque écriture renvoie la liste entière. */
export function fetchKitCosts(token: string, signal?: AbortSignal): Promise<KitCostPeriodsResponse> {
  return request<KitCostPeriodsResponse>('/api/settings/kit-costs', { token, signal });
}

export function createKitCost(input: KitCostPeriodInput, token: string): Promise<KitCostPeriodsResponse> {
  return request<KitCostPeriodsResponse>('/api/settings/kit-costs', { method: 'POST', body: input, token });
}

export function updateKitCost(id: number, input: KitCostPeriodInput, token: string): Promise<KitCostPeriodsResponse> {
  return request<KitCostPeriodsResponse>(`/api/settings/kit-costs/${id}`, { method: 'PUT', body: input, token });
}

export function deleteKitCost(id: number, token: string): Promise<KitCostPeriodsResponse> {
  return request<KitCostPeriodsResponse>(`/api/settings/kit-costs/${id}`, { method: 'DELETE', token });
}

/** Coût d'un article (variante Shopify) saisi dans le dashboard. Chaque écriture renvoie la liste entière. */
export function fetchArticleCosts(token: string, signal?: AbortSignal): Promise<ArticleCostsResponse> {
  return request<ArticleCostsResponse>('/api/settings/article-costs', { token, signal });
}

export function putArticleCost(variantId: string, input: ArticleCostInput, token: string): Promise<ArticleCostsResponse> {
  return request<ArticleCostsResponse>(`/api/settings/article-costs/${variantId}`, { method: 'PUT', body: input, token });
}

/** Charges fixes : répartition sur la période, et saisie. */
export function fetchChargesStats(query: PeriodQuery, token: string, signal?: AbortSignal): Promise<ChargesStatsResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  return request<ChargesStatsResponse>(`/api/charges/stats?${params.toString()}`, { token, signal });
}

export function createCharge(input: FixedChargeInput, token: string): Promise<FixedChargeResponse> {
  return request<FixedChargeResponse>('/api/charges', { method: 'POST', body: input, token });
}

export function updateCharge(id: number, input: FixedChargeInput, token: string): Promise<FixedChargeResponse> {
  return request<FixedChargeResponse>(`/api/charges/${id}`, { method: 'PUT', body: input, token });
}

export function deleteCharge(id: number, token: string): Promise<void> {
  return request<void>(`/api/charges/${id}`, { method: 'DELETE', token });
}

/** SAV : statistiques des tickets Gorgias (depuis la base). */
export function fetchSavStats(query: PeriodQuery, token: string, signal?: AbortSignal): Promise<SavStatsResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  return request<SavStatsResponse>(`/api/sav/stats?${params.toString()}`, { token, signal });
}

/** SAV : derniers messages, lus EN DIRECT chez Gorgias (jamais stockés). */
export function fetchSavMessages(cursor: string | null, token: string, signal?: AbortSignal): Promise<SavMessagesResponse> {
  const params = new URLSearchParams(cursor ? { cursor } : {});
  return request<SavMessagesResponse>(`/api/sav/messages${cursor ? `?${params.toString()}` : ''}`, { token, signal });
}

/** Sessions GA4 synchronisées, jour par jour (Résumé). */
export function fetchTrafficStats(query: PeriodQuery, token: string, signal?: AbortSignal): Promise<TrafficStatsResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  return request<TrafficStatsResponse>(`/api/traffic/stats?${params.toString()}`, { token, signal });
}

/** Page Analytics (GA4) : rapports lus en direct chez GA4. */
export function fetchGa4Overview(query: PeriodQuery, token: string, signal?: AbortSignal): Promise<Ga4OverviewResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  return request<Ga4OverviewResponse>(`/api/traffic/ga4?${params.toString()}`, { token, signal });
}

/** Branding (MC3) : répartition sur la période, et saisie. */
export function fetchBrandingStats(query: PeriodQuery, token: string, signal?: AbortSignal): Promise<BrandingStatsResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  return request<BrandingStatsResponse>(`/api/branding/stats?${params.toString()}`, { token, signal });
}

export function createBranding(input: BrandingExpenseInput, token: string): Promise<BrandingExpenseResponse> {
  return request<BrandingExpenseResponse>('/api/branding', { method: 'POST', body: input, token });
}

export function updateBranding(id: number, input: BrandingExpenseInput, token: string): Promise<BrandingExpenseResponse> {
  return request<BrandingExpenseResponse>(`/api/branding/${id}`, { method: 'PUT', body: input, token });
}

export function deleteBranding(id: number, token: string): Promise<void> {
  return request<void>(`/api/branding/${id}`, { method: 'DELETE', token });
}

// --- Assistant (chat IA) -----------------------------------------------------

/**
 * Pose une question à l'assistant et reçoit la réponse au fil de l'eau.
 *
 * `POST /api/chat` répond en SSE : une ligne `data: {ChatStreamEvent}` par
 * évènement. `fetch` + lecture du flux plutôt qu'`EventSource`, qui ne sait
 * ni envoyer un corps ni un en-tête Authorization. La promesse se résout quand
 * le flux est terminé ; les erreurs HTTP (401, 503 sans clé…) lèvent une
 * `ApiRequestError` comme les autres appels.
 */
export async function streamChat(
  body: ChatRequest,
  token: string,
  onEvent: (event: ChatStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiRequestError(0, 'API injoignable.');
  }
  if (!response.ok) {
    let message = `Erreur ${response.status}`;
    try {
      const payload = (await response.json()) as { error?: unknown };
      if (typeof payload.error === 'string' && payload.error) message = payload.error;
    } catch {
      // Corps illisible : statut seul.
    }
    throw new ApiRequestError(response.status, message);
  }
  if (!response.body) throw new ApiRequestError(0, 'Réponse sans flux.');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // Un évènement SSE se termine par une ligne vide ; le reste attend la suite.
    let cut = buffer.indexOf('\n\n');
    while (cut >= 0) {
      const chunk = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      for (const line of chunk.split('\n')) {
        if (!line.startsWith('data:')) continue;
        try {
          onEvent(JSON.parse(line.slice(5).trim()) as ChatStreamEvent);
        } catch {
          // Ligne tronquée ou inattendue : ignorée, le flux continue.
        }
      }
      cut = buffer.indexOf('\n\n');
    }
  }
}
